import React, { useState, useEffect, useCallback, useMemo } from "react";
import AuthLink from "../../Auth/AuthLink";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { FacebookShareButton } from "react-share";
import {
  ArrowUp,
  Brain,
  ChevronRight,
  Facebook,
  FileEdit,
  Loader2,
  CheckCircle2,
  LayoutGrid,
  Smartphone,
  Lock,
} from "lucide-react";

import api from "./../../../const/axios";
import history from "./../../../const/history";
import { withRouter } from "./../../../utils/withRouter";
import { loadingOff, loadingOn } from "./../../../redux/actions";
import { connect } from "react-redux";

import Questions from "./Questions";
import Themes from "./Themes";
import Quizzes from "./Quizzes";
import PackageTrainerOverlay from "./PackageTrainerOverlay";
import { DiscussionProvider, DiscussionEntityType } from "./DiscussionContext";
import { isFeatureEnabled } from "./../../../services/flagsService";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SubPhase {
  id: number;
  name: string;
  questions?: any[];
  themes?: any[];
}

interface Phase {
  id: number;
  name: string;
  subs?: SubPhase[];
  questions?: any[];
  themes?: any[];
}

interface PackageData {
  package?: {
    name: string;
    game: { id: number; name: string; with_Theme: boolean };
    phases: Phase[];
    editors: { fullname: string }[];
    tags: { id: number; name: string }[];
    played: string;
    added: string;
    information?: string;
  };
  tournament?: {
    name: string;
    isLeague: boolean;
    continuation?: string;
    organizations: { name: string }[];
    organizers: { fullname: string }[];
  };
  user?: { fullname: string };
  copies?: any[];
  viewtype?: number;
}

interface PackageLiveResultsInfo {
  id: number;
  slug: string;
  name: string;
  date: string;
  isApproved: boolean | null;
  isCompleted: boolean;
  hasResults: boolean;
}

// ─── Phase sidebar ────────────────────────────────────────────────────────────

interface PhaseSidebarProps {
  phases: Phase[];
  mainPhase: number;
  subPhase: number | null;
  onSelect: (main: number, sub: number | null) => void;
}

const PhaseSidebar: React.FC<PhaseSidebarProps> = ({
  phases,
  mainPhase,
  subPhase,
  onSelect,
}) => {
  const [expanded, setExpanded] = useState<number | null>(null);

  // Initialize: only the first parent round (with sub-phases) is open
  useEffect(() => {
    const firstWithSubs = phases.find(p => p.subs && p.subs.length > 0);
    setExpanded(firstWithSubs ? firstWithSubs.id : null);
  }, [phases]);

  // Auto-expand the parent that contains the active sub-phase
  useEffect(() => {
    if (subPhase === null) return;
    setExpanded(mainPhase);
  }, [mainPhase, subPhase]);

  const toggleExpanded = (id: number) => {
    setExpanded(prev => (prev === id ? null : id));
  };

  return (
    <nav className="space-y-1">
      {phases.map(phase => {
        const hasSubs = phase.subs && phase.subs.length > 0;
        const isMainActive = phase.id === mainPhase;
        const isExpanded = expanded === phase.id;

        return (
          <div key={phase.id}>
            {/* Parent phase */}
            <button
              onClick={() => {
                if (hasSubs) toggleExpanded(phase.id);
                else onSelect(phase.id, null);
              }}
              aria-expanded={hasSubs ? isExpanded : undefined}
              className={cn(
                "w-full text-left px-3 py-2 rounded-lg text-sm font-semibold flex items-center justify-between transition-all duration-150",
                isMainActive && !hasSubs
                  ? "bg-blue-600 text-white shadow-sm shadow-blue-200"
                  : hasSubs
                  ? "text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
              )}
            >
              <span>{phase.name}</span>
              {hasSubs && (
                <ChevronRight
                  className={cn(
                    "w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform duration-200",
                    isExpanded && "rotate-90"
                  )}
                />
              )}
            </button>

            {/* Sub-phases */}
            {hasSubs && isExpanded && (
              <div className="ml-3 mt-1 space-y-1 border-l-2 border-gray-100 dark:border-gray-800 pl-2">
                {phase.subs!.map(sub => {
                  const isActive = isMainActive && sub.id === subPhase;
                  return (
                    <button
                      key={sub.id}
                      onClick={() => onSelect(phase.id, sub.id)}
                      className={cn(
                        "w-full text-left px-2 py-1.5 rounded-md text-sm font-medium transition-all duration-150",
                        isActive
                          ? "bg-blue-600 text-white shadow-sm"
                          : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:text-gray-100"
                      )}
                    >
                      {sub.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

// ─── Metadata row ─────────────────────────────────────────────────────────────

const MetaRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex flex-wrap gap-1 text-sm">
    <span className="font-bold text-gray-800 dark:text-gray-100">{label}</span>
    <span className="text-gray-600 dark:text-gray-300">{value}</span>
  </div>
);

// ─── Back to top ──────────────────────────────────────────────────────────────

const BackToTop: React.FC = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-6 right-[26px] z-50 w-10 h-10 rounded-full bg-blue-600 text-white shadow-lg flex items-center justify-center hover:bg-blue-700 transition-all duration-200 hover:-translate-y-0.5"
      aria-label="Yuxarıya qayıt"
    >
      <ArrowUp className="w-4 h-4" />
    </button>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

interface PackageProps {
  params: { id: string };
  user: { isLoggedIn: boolean; data: any };
  loadingOn: () => void;
  loadingOff: () => void;
}

const Package: React.FC<PackageProps> = ({ params, user, loadingOn, loadingOff }) => {
  const { t } = useTranslation();

  const [data, setData] = useState<PackageData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [mobileOnly, setMobileOnly] = useState(false);
  const [mainPhase, setMainPhase] = useState<number>(0);
  const [subPhase, setSubPhase] = useState<number | null>(null);
  const [selectedPhase, setSelectedPhase] = useState<any>({});
  const [liveResults, setLiveResults] = useState<PackageLiveResultsInfo | null>(null);
  const [trainerOpen, setTrainerOpen] = useState(false);

  // Edit request modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);

  // Load package data
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      loadingOn();
      try {
        let res;
        try {
          res = await api.get(`/packages/foruse?id=${params.id}`);
        } catch (err: any) {
          if (cancelled) return;
          if (err?.response?.status === 403 && err?.response?.data?.error === "mobile_only") {
            setMobileOnly(true);
            setIsLoading(false);
            return;
          }
          throw err;
        }
        if (cancelled) return;
        if (res.data === null) {
          history.push("/");
          return;
        }
        const phases: Phase[] = res.data.package.phases;
        const firstPhase = phases[0];
        const firstMain = firstPhase ? firstPhase.id : 0;
        const firstSub =
          firstPhase?.subs?.length ? firstPhase.subs[0].id : null;

        let selected: any = {};
        if (firstSub === null) {
          selected = phases.find(p => p.id === firstMain) ?? {};
        } else {
          selected =
            phases.find(p => p.id === firstMain)?.subs?.find(s => s.id === firstSub) ?? {};
        }

        setData({ ...res.data });
        setMainPhase(firstMain);
        setSubPhase(firstSub);
        setSelectedPhase(selected);
        setIsLoading(false);
        // Content height changes once loaded; re-assert top after it renders
        requestAnimationFrame(() => window.scrollTo(0, 0));
      } finally {
        if (!cancelled) loadingOff();
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  // Load public live-game results mapping for this package (if exists).
  useEffect(() => {
    let cancelled = false;
    api.get(`organizer-games/by-package/${params.id}`)
      .then((res) => {
        if (cancelled) return;
        setLiveResults(res.data ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setLiveResults(null);
      });

    return () => {
      cancelled = true;
    };
  }, [params.id]);

  // Phase selection
  const handlePhaseSelect = useCallback(
    (main: number, sub: number | null) => {
      if (!data.package) return;
      let selected: any = {};
      if (sub === null) {
        selected = data.package.phases.find(p => p.id === main) ?? {};
      } else {
        selected =
          data.package.phases
            .find(p => p.id === main)
            ?.subs?.find(s => s.id === sub) ?? {};
      }
      setMainPhase(main);
      setSubPhase(sub);
      setSelectedPhase(selected);
    },
    [data.package]
  );

  // Send edit request
  const sendEditRequest = async () => {
    if (!editText || editText.length <= 30) {
      // Minimal inline feedback — just shake or show inline warning
      return;
    }
    setSendLoading(true);
    try {
      await api.post("contacts", {
        name: user.data.name,
        surname: user.data.surname,
        email: user.data.email,
        phone: user.data.phone,
        isHandled: false,
        message: editText,
        topic: `Paketə düzəliş: ID = ${params.id}`,
      });
      setSendSuccess(true);
      setTimeout(() => {
        setModalOpen(false);
        setSendSuccess(false);
        setEditText("");
      }, 1500);
    } finally {
      setSendLoading(false);
    }
  };

  // ── Derived display values ──────────────────────────────────────────────

  const pkg = data.package;
  const tournament = data.tournament;
  const canOpenTrainer = Boolean(pkg?.game?.id === 1) && isFeatureEnabled('TRAINER_MODE');

  let name = "";
  if (tournament?.isLeague && tournament?.continuation) {
    name += tournament.continuation + " / ";
  }
  if (tournament) name += tournament.name + " ";
  if (pkg?.name) name += " / " + pkg.name;
  const gameName = pkg?.game?.name ?? "";

  // Dynamic description for meta tags
  const getFullDescription = (): string => {
    if (!pkg) return "";
    let qStr = "";
    if (pkg.game?.with_Theme) {
      const themesCount = pkg.phases.reduce((acc, p) => acc + (p.themes?.length ?? 0) + (p.subs?.reduce((sAcc, s) => sAcc + (s.themes?.length ?? 0), 0) ?? 0), 0);
      qStr = `${themesCount} mövzu`;
    } else {
      const questionsCount = pkg.phases.reduce((acc, p) => acc + (p.questions?.length ?? 0) + (p.subs?.reduce((sAcc, s) => sAcc + (s.questions?.length ?? 0), 0) ?? 0), 0);
      qStr = `${questionsCount} sual`;
    }
    const tPart = tournament ? `${tournament.name}. ` : "";
    const sectionPart = gameName ? `${gameName} üzrə ` : "";
    return `${tPart}${sectionPart}${qStr} olan sual və mövzu arxivi. 3sual.az istinad məlumat bazası.`;
  };

  // Authors deduped
  const getAuthors = (): string[] => {
    if (!pkg) return [];
    const arr: string[] = [];
    pkg.phases.forEach(p => {
      if (!p.subs) {
        (pkg.game.with_Theme ? p.themes ?? [] : p.questions ?? []).forEach((item: any) => {
          item.authors?.forEach((a: any) => {
            if (!arr.includes(a.fullname)) arr.push(a.fullname);
          });
        });
      } else {
        p.subs.forEach(ph => {
          (pkg.game.with_Theme ? (ph as any).themes ?? [] : (ph as any).questions ?? []).forEach((item: any) => {
            item.authors?.forEach((a: any) => {
              if (!arr.includes(a.fullname)) arr.push(a.fullname);
            });
          });
        });
      }
    });
    return arr;
  };

  const discussionEntityType: DiscussionEntityType | null = useMemo(() => {
    if (!pkg) return null;
    switch (pkg.game.id) {
      case 1:
      case 4:
      case 6:
        return "question";
      case 2:
      case 3:
      case 5:
        return "theme";
      default:
        return null;
    }
  }, [pkg]);

  const discussionIds = useMemo<number[]>(() => {
    if (!discussionEntityType || !selectedPhase) return [];
    if (discussionEntityType === "theme") {
      const themes: any[] = (selectedPhase as any).themes ?? [];
      return themes.map((th) => th.id).filter((id): id is number => typeof id === "number");
    }
    const questions: any[] = (selectedPhase as any).questions ?? [];
    return questions
      .map((q) => q?.values?.[0]?.id)
      .filter((id): id is number => typeof id === "number");
  }, [discussionEntityType, selectedPhase]);

  const renderQuestions = () => {
    if (!pkg) return null;
    let content: React.ReactNode = null;
    switch (pkg.game.id) {
      case 1: content = <Questions phase={selectedPhase} />; break;
      case 2: content = <Themes type={true} phase={selectedPhase} />; break;
      case 3: content = <Themes type={false} phase={selectedPhase} />; break;
      case 4: content = <Questions phase={selectedPhase} />; break;
      case 5: content = <Themes type={true} erudit={true} phase={selectedPhase} />; break;
      case 6: content = <Questions phase={selectedPhase} />; break;
      case 7: content = <Quizzes type={true} erudit={true} phase={selectedPhase} />; break;
      default: return null;
    }
    if (!discussionEntityType) return content;
    return (
      <DiscussionProvider entityType={discussionEntityType} ids={discussionIds}>
        {content}
      </DiscussionProvider>
    );
  };

  const renderViewType = () => {
    if (!tournament || !data.viewtype) return null;
    const label = tournament.continuation ?? tournament.name;
    return (
      <Link
        to={`/packages/${params.id}/tournament`}
 className="group block bg-blue-50 dark:bg-blue-950 border border-blue-100 dark:border-gray-800 rounded-2xl p-4 transition-all duration-200 hover:bg-white dark:hover:bg-gray-900 hover:shadow-md mb-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white transition-transform group-hover:scale-105">
              <LayoutGrid className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900 dark:text-gray-50 leading-tight truncate text-wrap">
                {label}
              </p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-blue-400 transition-transform group-hover:translate-x-1 dark:text-indigo-300" />
        </div>
      </Link>
    );
  };

  const authors = isLoading ? [] : getAuthors();
  const orgCount = (tournament?.organizations.length ?? 0) + (tournament?.organizers.length ?? 0);

  // ── Mobile-only gate ───────────────────────────────────────────────────
  if (mobileOnly) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16 flex items-center justify-center">
        <div className="max-w-sm w-full mx-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-8 flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <Lock className="w-7 h-7 text-gray-500 dark:text-gray-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-50">Ödənişli paket</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
            Bu paket yalnız <span className="font-semibold text-gray-700 dark:text-gray-200">3Sual mobil tətbiqi</span> vasitəsilə əlçatandır. Satın almaq və baxmaq üçün tətbiqi yükləyin.
          </p>
          <div className="flex flex-col gap-2 w-full pt-2">
            <a
              href="https://apps.apple.com/app/3sual/id6744185965"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-black text-white text-sm font-semibold hover:bg-gray-800 transition-colors"
            >
              <Smartphone className="w-4 h-4" />
              App Store
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=az.sual3.mobile"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
            >
              <Smartphone className="w-4 h-4" />
              Google Play
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>
          {name} | Sual paketi — 3sual.az
        </title>
        <meta name="robots" content="index" />
        <link rel="canonical" href={`https://3sual.az/packages/${params.id}`} />
        <meta
          name="keywords"
          content="sual bazası, sual arxivi, məlumat bazası, ensiklopediya, istinad mənbəyi"
        />
        <meta
          name="description"
          content={getFullDescription()}
        />
        <meta
          property="og:url"
          content={`https://3sual.az/packages/${params.id}`}
        />
        <meta property="og:title" content={name} />
        <meta property="og:description" content={getFullDescription()} />
      </Helmet>

      {/* ── Edit request Dialog ──────────────────────────────────────── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("packages.editRequestTitle")}</DialogTitle>
          </DialogHeader>
          {sendSuccess ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
              <p className="text-gray-700 dark:text-gray-200 font-medium text-center">
                {t("packages.editRequestReceived")}
              </p>
            </div>
          ) : (
            <>
              <textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                rows={5}
                placeholder={t("packages.editPlaceholder")}
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 focus:border-blue-400 focus:bg-white dark:bg-gray-900 focus:outline-none transition-colors resize-none"
              />
              {editText.length > 0 && editText.length <= 30 && (
                <p className="text-xs text-amber-600 -mt-1">{t("packages.editWarning")}</p>
              )}
              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setModalOpen(false)}
                  disabled={sendLoading}
                >
                  {t("packages.cancel")}
                </Button>
                <Button
                  onClick={sendEditRequest}
                  disabled={sendLoading || editText.length <= 30}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                >
                  {sendLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t("packages.send")}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">

        {/* ── Header card ──────────────────────────────────────────────── */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 px-6 py-5 flex flex-col items-center text-center sm:flex-row sm:items-center sm:justify-between sm:text-left gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-gray-50 leading-tight capitalize">
              {name || "..."}
            </h1>
            {/* {gameName && (
              <span className="mt-1 inline-block text-xs font-semibold text-blue-500 bg-blue-50 dark:bg-blue-950 px-2.5 py-0.5 rounded-full dark:text-indigo-300">
                {gameName}
              </span>
            )} */}
          </div>
          <FacebookShareButton 
            url={`https://3sual.az/packages/${params.id}`}
          >
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1877F2] text-white text-sm font-semibold hover:bg-[#166FE5] transition-colors shadow-sm">
              <Facebook className="w-4 h-4" />
              Paylaş
            </div>
          </FacebookShareButton>
        </div>

        {/* ── Body: sidebar + content ───────────────────────────────────── */}
        <div className="flex flex-col lg:flex-row gap-4 lg:items-start">

          {/* Left: phase navigation */}
          <div className="w-full lg:w-72 flex-shrink-0 space-y-3">
            {renderViewType()}
            {!isLoading && pkg && (
              <>
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4">
                  <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">
                    Raundlar
                  </p>
                  <PhaseSidebar
                    phases={pkg.phases}
                    mainPhase={mainPhase}
                    subPhase={subPhase}
                    onSelect={handlePhaseSelect}
                  />
                </div>
                
                {/* Promo Banner as its own card below */}
                {isFeatureEnabled('PACKAGE_AD_BANNER') && (
                <div className="sticky top-20 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-4 text-center shadow-lg relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-20 h-20 bg-white dark:bg-gray-900/5 rounded-full -mr-10 -mt-10 transition-transform group-hover:scale-110" />
                  <p className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] mb-2 dark:text-indigo-300">
                    SƏNİN REKLAMIN
                  </p>
                  <p className="text-sm font-bold text-white leading-tight mb-3">
                    Burada ola bilər
                  </p>
                  <Link 
                    to="/contact"
                    className="inline-block w-full py-2 bg-blue-600 text-white hover:text-white text-xs font-bold rounded-lg"
                  >
                    Əlaqə
                  </Link>
                </div>
                )}
              </>
            )}
          </div>

          {/* Right: metadata + question content */}
          <div className="flex-1 min-w-0 space-y-4">

            {/* Metadata card */}
            {pkg && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5 relative">
                {/* Edit request button */}
                {user.isLoggedIn && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => setModalOpen(true)}
                          className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-blue-500 transition-colors dark:hover:text-indigo-200"
                          aria-label={t("packages.editNeeded")}
                        >
                          <FileEdit className="w-5 h-5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{t("packages.editNeeded")}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                <div className="space-y-2 pr-7">

                  {/* Organizer */}
                  {tournament && orgCount > 0 && (
                    <MetaRow
                      label={orgCount > 1 ? t("packages.organizerPlural") : t("packages.organizerSingular")}
                      value={[
                        ...tournament.organizations.map(e => e.name),
                        ...tournament.organizers.map(e => e.fullname),
                      ].join(", ")}
                    />
                  )}
                  {/* Editors */}
                  {pkg.editors.length > 0 && (
                    <MetaRow
                      label={pkg.editors.length > 1 ? t("packages.editorPlural") : t("packages.editorSingular")}
                      value={pkg.editors.map(e => e.fullname).join(", ")}
                    />
                  )}
                  {/* Authors */}
                  {authors.length > 0 && (
                    <MetaRow label="Müəlliflər:" value={authors.join(", ")} />
                  )}
                  {/* Game type */}
                  {gameName && (
                    <MetaRow label="Oyun tipi:" value={gameName} />
                  )}
                  {/* Dates */}
                  <MetaRow label="Oynanıldığı tarix:" value={pkg.played.substring(0, 10)} />
                  <MetaRow label="Əlavə edildiyi tarix:" value={pkg.added.substring(0, 10)} />
                  {/* Results link */}
                  {liveResults?.hasResults && (
                    <MetaRow
                      label="Nəticələr:"
                      value={
                        <Link
                          to={`/games/${liveResults.slug}`}
                          className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-semibold dark:text-indigo-300 dark:hover:text-indigo-200"
                        >
                          Turnir nəticələrinə bax
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      }
                    />
                  )}
                  {/* Info */}
                  {pkg.information && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{pkg.information}</p>
                  )}
                </div>
              </div>
            )}

            {/* Trainer mode feature card */}
            {canOpenTrainer && (
              user.isLoggedIn ? (
                <button
                  onClick={() => setTrainerOpen(true)}
                  className="group w-full text-left bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-2xl border border-blue-500/30 shadow-sm px-6 py-5 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-white dark:bg-gray-900/15 flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs font-bold text-blue-200 uppercase tracking-wider">Yeni xüsusiyyət</p>
                        <span className="text-[10px] font-semibold bg-white dark:bg-gray-900/20 text-white px-1.5 py-0.5 rounded-full">Beta</span>
                      </div>
                      <h3 className="text-base font-bold text-white leading-tight mb-1">Məşq rejimi</h3>
                      <p className="text-sm text-blue-100 leading-snug">
                        Sualları bir-bir məşq edin, vaxt limitlə çalışın, nəticələrinizi izləyin.
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-blue-200 flex-shrink-0 self-center transition-transform group-hover:translate-x-1" />
                  </div>
                </button>
              ) : !isFeatureEnabled('USER_ACCOUNTS') ? null : (
                <AuthLink
                  mode="login"
 className="group flex items-start gap-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
                >
                  <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-950 flex items-center justify-center flex-shrink-0">
                    <Brain className="w-6 h-6 text-blue-500 dark:text-indigo-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-xs font-bold text-blue-400 uppercase tracking-wider dark:text-indigo-300">Xüsusiyyət</p>
                      <span className="text-[10px] font-semibold bg-blue-50 dark:bg-blue-950 text-blue-500 px-1.5 py-0.5 rounded-full border border-blue-100 dark:border-blue-900 dark:text-indigo-300">Beta</span>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 dark:text-gray-50 leading-tight mb-1">Trainer Mode</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-snug">
                      Sualları məşq etmək üçün daxil olun — pulsuz, qeydiyyat tələb olunur.
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0 self-center transition-transform group-hover:translate-x-1 group-hover:text-blue-400 dark:group-hover:text-indigo-200" />
                </AuthLink>
              )
            )}

            {/* Questions / Themes / Quizzes card */}
            {pkg && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
                {renderQuestions()}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer: tags + added by ────────────────────────────────────── */}
        {pkg && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {pkg.tags.map(tag => {
                const badge = (
                  <Badge
                    key={tag.id}
                    className="bg-blue-50 dark:bg-blue-950 text-blue-700 font-semibold text-xs hover:bg-blue-100 dark:text-indigo-300"
                  >
                    {tag.name}
                  </Badge>
                )
                return isFeatureEnabled("PACKAGE_TAG_PAGES") ? (
                  <Link key={tag.id} to={`/tags/${tag.id}`}>{badge}</Link>
                ) : badge
              })}
            </div>
            {data.user && (
              <p className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                Paketi əlavə etdi:{" "}
                <span className="font-bold text-gray-800 dark:text-gray-100">{data.user.fullname}</span>
              </p>
            )}
          </div>
        )}
      </div>

      {pkg && canOpenTrainer && (
        <PackageTrainerOverlay
          open={trainerOpen}
          onClose={() => setTrainerOpen(false)}
          packageId={Number(params.id)}
          packageName={name || pkg.name}
          phases={pkg.phases}
          isLoggedIn={user.isLoggedIn}
          currentUserId={user?.data?.id ? Number(user.data.id) : null}
        />
      )}

      <BackToTop />
    </div>
  );
};

// ─── Redux wiring ────────────────────────────────────────────────────────────

const mapStateToProps = ({ user }: any) => ({ user });

export default withRouter(
  connect(mapStateToProps, { loadingOn, loadingOff })(Package)
);
